import type { AdditionalFieldDelegate } from "@firecms/core";
import {
  Button,
  IconButton,
  PersonIcon,
  Tooltip,
} from "@firecms/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AdminModal } from "../custom/AdminModal";
import type { EventUser } from "../collections/eventUsers";
import type { AuthUser } from "../firebase/authUsers";
import {
  peekAuthUserCache,
  requestAuthUser,
} from "../firebase/authUserBatcher";
import type { AdminTranslateFn } from "../i18n/getAdminT";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import { copyTextToClipboard } from "../workspace/adminLinks";

function formatAuthDate(
  value: string | null | undefined,
  locale: string,
  neverLabel: string
): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return neverLabel;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function providerLabel(providerId: string, t: AdminTranslateFn): string {
  switch (providerId) {
    case "google.com":
      return t("eventUserProfile.providerGoogle");
    case "password":
      return t("eventUserProfile.providerPassword");
    case "apple.com":
      return t("eventUserProfile.providerApple");
    case "phone":
      return t("eventUserProfile.providerPhone");
    default:
      return t("eventUserProfile.providerOther", { provider: providerId });
  }
}

function StatusChip({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "danger" | "neutral";
  children: ReactNode;
}) {
  const toneClass =
    tone === "ok"
      ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200"
      : tone === "warn"
        ? "bg-amber-50 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
        : tone === "danger"
          ? "bg-rose-50 text-rose-800 dark:bg-rose-950/50 dark:text-rose-200"
          : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";

  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        toneClass,
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function ProfileAvatar({ user }: { user: AuthUser }) {
  const initial = (
    user.displayName?.trim()?.[0] ||
    user.email?.trim()?.[0] ||
    "?"
  ).toUpperCase();

  if (user.photoURL) {
    return (
      <img
        src={user.photoURL}
        alt=""
        className="h-16 w-16 rounded-full object-cover ring-2 ring-white dark:ring-slate-800"
      />
    );
  }

  return (
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 text-xl font-semibold text-slate-700 ring-2 ring-white dark:bg-slate-600 dark:text-slate-100 dark:ring-slate-800">
      {initial}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-sm text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="text-sm font-medium text-slate-900 dark:text-slate-100 sm:text-right">
        {value}
      </dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h3>
      <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
        {children}
      </div>
    </section>
  );
}

function AuthUserProfileDialog({
  user,
  onClose,
  t,
  locale,
}: {
  user: AuthUser;
  onClose: () => void;
  t: AdminTranslateFn;
  locale: string;
}) {
  const [copied, setCopied] = useState(false);
  const title = user.displayName?.trim() || user.email || t("eventUserProfile.noName");
  const never = t("eventUserProfile.never");

  return (
    <AdminModal
      open
      onClose={onClose}
      ariaLabel={t("eventUserProfile.dialogAria")}
      title={t("eventUserProfile.dialogTitle")}
      maxWidth="lg"
      bodyClassName="px-5 py-5"
      footer={
        <Button onClick={onClose} variant="outlined">
          {t("eventUserProfile.close")}
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-4">
          <ProfileAvatar user={user} />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="truncate text-lg font-semibold text-slate-900 dark:text-slate-50">
              {title}
            </div>
            {user.displayName?.trim() && user.email ? (
              <div className="truncate text-sm text-slate-600 dark:text-slate-300">
                {user.email}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {user.disabled ? (
                <StatusChip tone="danger">
                  {t("eventUserProfile.accountDisabled")}
                </StatusChip>
              ) : (
                <StatusChip tone="ok">
                  {t("eventUserProfile.accountActive")}
                </StatusChip>
              )}
              {user.email ? (
                user.emailVerified ? (
                  <StatusChip tone="ok">
                    {t("eventUserProfile.emailVerified")}
                  </StatusChip>
                ) : (
                  <StatusChip tone="warn">
                    {t("eventUserProfile.emailUnverified")}
                  </StatusChip>
                )
              ) : null}
            </div>
          </div>
        </div>

        <Section title={t("eventUserProfile.sectionContact")}>
          <dl className="space-y-3">
            <DetailRow
              label={t("eventUserProfile.email")}
              value={user.email ?? "—"}
            />
            <DetailRow
              label={t("eventUserProfile.phone")}
              value={user.phoneNumber ?? t("eventUserProfile.noPhone")}
            />
          </dl>
        </Section>

        <Section title={t("eventUserProfile.sectionSignIn")}>
          {user.providerData.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t("eventUserProfile.noProviders")}
            </p>
          ) : (
            <ul className="space-y-2">
              {user.providerData.map((provider) => (
                <li
                  key={`${provider.providerId}:${provider.uid}`}
                  className="flex flex-col gap-0.5 rounded-md bg-white/70 px-3 py-2 dark:bg-slate-800/70"
                >
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {providerLabel(provider.providerId, t)}
                  </span>
                  {(provider.email || provider.displayName) && (
                    <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {provider.email || provider.displayName}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={t("eventUserProfile.sectionActivity")}>
          <dl className="space-y-3">
            <DetailRow
              label={t("eventUserProfile.createdAt")}
              value={formatAuthDate(user.metadata.creationTime, locale, never)}
            />
            <DetailRow
              label={t("eventUserProfile.lastSignIn")}
              value={formatAuthDate(
                user.metadata.lastSignInTime,
                locale,
                never
              )}
            />
            <DetailRow
              label={t("eventUserProfile.lastActive")}
              value={formatAuthDate(
                user.metadata.lastRefreshTime,
                locale,
                never
              )}
            />
          </dl>
        </Section>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-slate-200 px-3 py-2 dark:border-slate-700">
          <div className="min-w-0">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {t("eventUserProfile.userId")}
            </div>
            <code className="block truncate text-xs text-slate-700 dark:text-slate-300">
              {user.uid}
            </code>
          </div>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              void copyTextToClipboard(user.uid).then((ok) => {
                if (!ok) {
                  return;
                }
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            {copied
              ? t("eventUserProfile.copied")
              : t("eventUserProfile.copyId")}
          </Button>
        </div>
      </div>
    </AdminModal>
  );
}

function EventUserProfileCell({
  accountId,
  eventId,
  uid,
}: {
  accountId: string;
  eventId: string;
  uid: string;
}) {
  const locale = getAdminUiLocale();
  const t = getAdminT(locale);
  const [auth, setAuth] = useState<AuthUser | null | undefined>(() =>
    peekAuthUserCache(accountId, eventId, uid)
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void requestAuthUser(accountId, eventId, uid).then((user) => {
      if (alive) {
        setAuth(user);
      }
    });
    return () => {
      alive = false;
    };
  }, [accountId, eventId, uid]);

  const label = useMemo(() => {
    if (!auth?.email) {
      return "—";
    }
    return auth.email;
  }, [auth]);

  if (auth === undefined) {
    return (
      <div
        className="flex min-w-0 items-center gap-2 py-0.5"
        aria-busy="true"
        aria-label={t("eventUserProfile.column")}
      >
        <div className="h-4 w-40 max-w-full animate-pulse rounded bg-surface-200/70 dark:bg-surface-700" />
        <div className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-surface-200/70 dark:bg-surface-700" />
      </div>
    );
  }

  return (
    <>
      <div className="flex min-w-0 items-center gap-1">
        <span className="truncate text-sm" title={label}>
          {label}
        </span>
        <Tooltip title={t("eventUserProfile.viewProfile")}>
          <IconButton
            aria-label={t("eventUserProfile.viewProfile")}
            size="small"
            disabled={!auth}
            onClick={(event) => {
              event.stopPropagation();
              setDialogOpen(true);
            }}
          >
            <PersonIcon />
          </IconButton>
        </Tooltip>
      </div>

      {dialogOpen && auth ? (
        <AuthUserProfileDialog
          user={auth}
          onClose={() => setDialogOpen(false)}
          t={t}
          locale={locale}
        />
      ) : null}
    </>
  );
}

export function buildEventUserProfileField(
  accountId: string,
  eventId: string
): AdditionalFieldDelegate<EventUser> {
  const t = getAdminT(getAdminUiLocale());

  return {
    key: "profile",
    name: t("eventUserProfile.column"),
    width: 260,
    Builder: ({ entity }) => {
      const memberUid = entity.values.uid || entity.id;
      return (
        <EventUserProfileCell
          accountId={accountId}
          eventId={eventId}
          uid={memberUid}
        />
      );
    },
  };
}
